import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { apiGet } from '../client'
import { dealsKey } from './deals'
import type { AnalysisJobListItem, JobStatus } from '../types'

/**
 * Which statuses mean "still running" is a backend fact. It is derived from the
 * generated union rather than listed here: the old frontend kept its own list
 * and it drifted, naming two statuses the backend has never produced.
 */
const TERMINAL: ReadonlySet<JobStatus> = new Set<JobStatus>(['completed', 'failed'])

/** How often to ask while something is running. Nothing is asked when nothing is. */
const POLL_MS = 3000

export const jobsKey = ['analysis-jobs'] as const

/**
 * How many analyses are running, and — while any are — the thing that makes the
 * board update on its own.
 *
 * The upload dialog says "You can close this and keep working — the deal appears
 * on the board when it is done." That was not true. Closing the dialog unmounted
 * the per-job poll with it, this query had no interval of its own, and nothing
 * invalidated the deals when the analysis finished. The tile sat on "1 running"
 * and the new deal did not appear until something else forced a refetch — a
 * screen change, or the window regaining focus. Found by Mike on production,
 * 2026-09-03, on the first upload a new account had ever made.
 *
 * So the list polls itself while a job is live, and when the last one finishes
 * it invalidates the deals once. Both halves matter: without the interval the
 * count never changes, and without the invalidation the count changes while the
 * board still shows yesterday.
 */
export function useActiveJobCount() {
  const queryClient = useQueryClient()

  const query = useQuery<AnalysisJobListItem[], Error, number>({
    queryKey: jobsKey,
    queryFn: () => apiGet<AnalysisJobListItem[]>('/analysis/jobs'),
    select: (jobs) => jobs.filter((job) => !TERMINAL.has(job.status)).length,
    // Read off the RAW rows, not the selected number: `select` shapes what the
    // component sees, and `state.data` here is what the server sent.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((job) => !TERMINAL.has(job.status)) ? POLL_MS : false,
  })

  const active = query.data
  const previous = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (active === undefined) return
    // Only on the falling edge, and only to zero: invalidating on every tick
    // would refetch the whole board every three seconds for as long as an
    // analysis runs.
    if ((previous.current ?? 0) > 0 && active === 0) {
      void queryClient.invalidateQueries({ queryKey: dealsKey })
    }
    previous.current = active
  }, [active, queryClient])

  return query
}
